import { useState, useCallback, useEffect, useRef } from 'react';
import { useThemeContext, theme as baseTheme, SIDEBAR_DARK_PRESET, SIDEBAR_LIGHT_PRESET, STORAGE_KEY } from '@/theme';
import { useTranslation } from '@/i18n';
import { ButtonSpinner } from '@/components/ui';
import { brandingService } from '@/api';
import type { ThemeOverride } from '@/theme';

// Icons
const Icons = {
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Save: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Download: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Upload: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Reset: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  ResetSmall: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  X: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Spinner: () => <ButtonSpinner />,
};

interface StyleConfigPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type SectionKey = 'colors' | 'sidebar' | 'typography' | 'spacing' | 'borders';

interface ColorInputProps {
  label: string;
  value: string;
  defaultValue?: string;
  description?: string;
  onChange: (value: string) => void;
}

const ColorInput = ({ label, value, defaultValue, description, onChange }: ColorInputProps) => {
  const { theme } = useThemeContext();
  const { t } = useTranslation();
  const isModified = defaultValue !== undefined && value !== defaultValue;

  // Convert hex to a valid color for the color picker (handle rgba/rgb values)
  const getColorPickerValue = (colorValue: string): string => {
    // If it's already a valid hex, return it
    if (/^#[0-9A-Fa-f]{6}$/.test(colorValue)) return colorValue;
    if (/^#[0-9A-Fa-f]{3}$/.test(colorValue)) {
      // Expand 3-digit hex to 6-digit
      const r = colorValue[1];
      const g = colorValue[2];
      const b = colorValue[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    // For rgba/rgb values, return a fallback (color picker only supports hex)
    return '#808080';
  };

  return (
    <div style={{ marginBottom: theme.spacing.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.xs }}>
        <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <span style={{ fontSize: theme.typography.fontSizeXs, color: isModified ? theme.colors.brandAccent : theme.colors.textMuted, fontFamily: 'monospace' }}>{value}</span>
          {isModified && (
            <button
              onClick={() => onChange(defaultValue)}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                color: theme.colors.textMuted,
                display: 'flex',
                alignItems: 'center',
                borderRadius: theme.borderRadius.sm,
              }}
              title={t('StyleConfigPanel:ResetToDefault')}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.brandAccent; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.colors.textMuted; }}
            >
              <Icons.ResetSmall />
            </button>
          )}
        </div>
      </div>
      {description && (
        <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>{description}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
        <input
          type="color"
          value={getColorPickerValue(value)}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '40px',
            height: '28px',
            border: 'none',
            borderRadius: theme.borderRadius.sm,
            cursor: 'pointer',
            padding: 0,
          }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            padding: `${theme.spacing.md} 10px`,
            border: `1px solid ${isModified ? theme.colors.brandAccent : theme.colors.border}`,
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSizeSm,
            fontFamily: 'monospace',
          }}
        />
      </div>
    </div>
  );
};

interface TextInputProps {
  label: string;
  value: string | number;
  defaultValue?: string | number;
  description?: string;
  onChange: (value: string) => void;
}

const TextInput = ({ label, value, defaultValue, description, onChange }: TextInputProps) => {
  const { theme } = useThemeContext();
  const { t } = useTranslation();
  const isModified = defaultValue !== undefined && String(value) !== String(defaultValue);

  return (
    <div style={{ marginBottom: theme.spacing.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.xs }}>
        <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <span style={{ fontSize: theme.typography.fontSizeXs, color: isModified ? theme.colors.brandAccent : theme.colors.textMuted, fontFamily: 'monospace' }}>{value}</span>
          {isModified && (
            <button
              onClick={() => onChange(String(defaultValue))}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                color: theme.colors.textMuted,
                display: 'flex',
                alignItems: 'center',
                borderRadius: theme.borderRadius.sm,
              }}
              title={t('StyleConfigPanel:ResetToDefault')}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.brandAccent; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.colors.textMuted; }}
            >
              <Icons.ResetSmall />
            </button>
          )}
        </div>
      </div>
      {description && (
        <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>{description}</div>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: `${theme.spacing.md} 10px`,
          border: `1px solid ${isModified ? theme.colors.brandAccent : theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          fontSize: theme.typography.fontSizeSm,
          fontFamily: 'monospace',
        }}
      />
    </div>
  );
};

export const StyleConfigPanel = ({ isOpen, onClose }: StyleConfigPanelProps) => {
  const { theme, setOverride, resetTheme, exportTheme, importTheme, hasOverride, saveThemeToBackend } = useThemeContext();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<SectionKey>('colors');
  const [localOverride, setLocalOverride] = useState<ThemeOverride>(() => {
    // Restore from sessionStorage so we don't lose prior edits after page refresh
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored) as ThemeOverride;
    } catch { /* ignore */ }
    return {};
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Logo upload state
  const [lightLogoFile, setLightLogoFile] = useState<File | null>(null);
  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);
  const [lightLogoPreview, setLightLogoPreview] = useState<string | null>(null);
  const [darkLogoPreview, setDarkLogoPreview] = useState<string | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [isUploadingLight, setIsUploadingLight] = useState(false);
  const [isUploadingDark, setIsUploadingDark] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const lightLogoInputRef = useRef<HTMLInputElement>(null);
  const darkLogoInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    brandingService.getLogoUrls().then((urls) => setBackgroundUrl(urls.backgroundImageUrl)).catch(() => undefined);
  }, [isOpen]);

  const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
  const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB
  const ALLOWED_BACKGROUND_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  const MAX_BACKGROUND_SIZE = 8 * 1024 * 1024; // 8MB

  const handleBackgroundFileSelect = useCallback((file: File | null) => {
    if (!file) return;
    if (!ALLOWED_BACKGROUND_TYPES.includes(file.type)) {
      setErrorMessage(t('StyleConfigPanel:InvalidBackgroundFileType', { defaultValue: 'Invalid file type. Allowed: PNG, JPEG, WEBP.' }));
      return;
    }
    if (file.size > MAX_BACKGROUND_SIZE) {
      setErrorMessage(t('StyleConfigPanel:BackgroundFileTooLarge', { defaultValue: 'File too large. Maximum size is 8MB.' }));
      return;
    }
    setErrorMessage(null);
    setBackgroundFile(file);
    setBackgroundPreview(URL.createObjectURL(file));
  }, [t]);

  const handleLogoFileSelect = useCallback((file: File | null, variant: 'light' | 'dark') => {
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setErrorMessage(t('StyleConfigPanel:InvalidFileType'));
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setErrorMessage(t('StyleConfigPanel:FileTooLarge'));
      return;
    }
    setErrorMessage(null);
    const previewUrl = URL.createObjectURL(file);
    if (variant === 'light') {
      setLightLogoFile(file);
      setLightLogoPreview(previewUrl);
    } else {
      setDarkLogoFile(file);
      setDarkLogoPreview(previewUrl);
    }
  }, [t]);

  // Update a color value
  const updateColor = useCallback(
    (key: keyof typeof theme.colors, value: string) => {
      const newOverride = {
        ...localOverride,
        colors: {
          ...localOverride.colors,
          [key]: value,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Update a typography value
  const updateTypography = useCallback(
    (key: keyof typeof theme.typography, value: string | number) => {
      const newOverride = {
        ...localOverride,
        typography: {
          ...localOverride.typography,
          [key]: value,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Update a spacing value
  const updateSpacing = useCallback(
    (key: keyof typeof theme.spacing, value: string) => {
      const newOverride = {
        ...localOverride,
        spacing: {
          ...localOverride.spacing,
          [key]: value,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Update a border radius value
  const updateBorderRadius = useCallback(
    (key: keyof typeof theme.borderRadius, value: string) => {
      const newOverride = {
        ...localOverride,
        borderRadius: {
          ...localOverride.borderRadius,
          [key]: value,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Update a sidebar value
  const updateSidebar = useCallback(
    (key: keyof typeof theme.sidebar, value: string) => {
      const newOverride = {
        ...localOverride,
        sidebar: {
          ...localOverride.sidebar,
          [key]: value,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Apply a sidebar mode preset (batch-update all sidebar colors + mode)
  const applySidebarPreset = useCallback(
    (mode: 'dark' | 'light') => {
      const preset = mode === 'dark' ? SIDEBAR_DARK_PRESET : SIDEBAR_LIGHT_PRESET;
      const newOverride = {
        ...localOverride,
        sidebar: {
          ...localOverride.sidebar,
          ...preset,
          sidebarMode: mode,
        },
      };
      setLocalOverride(newOverride);
      setOverride(newOverride);
    },
    [localOverride, setOverride]
  );

  // Logo upload/remove handlers (must be after updateSidebar)
  const handleLogoUpload = useCallback(async (variant: 'light' | 'dark') => {
    const file = variant === 'light' ? lightLogoFile : darkLogoFile;
    if (!file) return;
    const setUploading = variant === 'light' ? setIsUploadingLight : setIsUploadingDark;
    setUploading(true);
    setErrorMessage(null);
    try {
      const result = await brandingService.uploadLogo(file, variant);
      updateSidebar(variant === 'light' ? 'logoLightUrl' : 'logoDarkUrl', result.url);
      if (variant === 'light') {
        setLightLogoFile(null);
        setLightLogoPreview(null);
      } else {
        setDarkLogoFile(null);
        setDarkLogoPreview(null);
      }
    } catch {
      setErrorMessage(`Failed to upload ${variant} logo.`);
    } finally {
      setUploading(false);
    }
  }, [lightLogoFile, darkLogoFile, updateSidebar]);

  const handleLogoRemove = useCallback(async (variant: 'light' | 'dark') => {
    try {
      await brandingService.deleteLogo(variant);
      updateSidebar(variant === 'light' ? 'logoLightUrl' : 'logoDarkUrl', '');
    } catch {
      setErrorMessage(`Failed to remove ${variant} logo.`);
    }
  }, [updateSidebar]);

  const handleBackgroundUpload = useCallback(async () => {
    if (!backgroundFile) return;
    setIsUploadingBackground(true);
    setErrorMessage(null);
    try {
      const result = await brandingService.uploadBackground(backgroundFile);
      setBackgroundUrl(result.url);
      setBackgroundFile(null);
      setBackgroundPreview(null);
    } catch {
      setErrorMessage(t('StyleConfigPanel:FailedUploadBackground', { defaultValue: 'Failed to upload login background.' }));
    } finally {
      setIsUploadingBackground(false);
    }
  }, [backgroundFile, t]);

  const handleBackgroundRemove = useCallback(async () => {
    try {
      await brandingService.deleteBackground();
      setBackgroundUrl(null);
      setBackgroundFile(null);
      setBackgroundPreview(null);
    } catch {
      setErrorMessage(t('StyleConfigPanel:FailedRemoveBackground', { defaultValue: 'Failed to remove login background.' }));
    }
  }, [t]);

  // Export theme
  const handleExport = useCallback(() => {
    const json = exportTheme();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'motr-theme.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [exportTheme]);

  // Import theme
  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const content = ev.target?.result as string;
          if (importTheme(content)) {
            setLocalOverride({});
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, [importTheme]);

  // Reset theme
  const handleReset = useCallback(() => {
    resetTheme();
    setLocalOverride({});
    setSaveStatus('idle');
  }, [resetTheme]);

  // Save theme to backend
  const handleSaveToBackend = useCallback(async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage(null);
    try {
      const success = await saveThemeToBackend();
      setSaveStatus(success ? 'success' : 'error');
      if (success) {
        // Clear success status after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        setErrorMessage(t('StyleConfigPanel:FailedSaveTheme'));
      }
    } catch (error) {
      setSaveStatus('error');
      const message = error instanceof Error ? error.message : t('StyleConfigPanel:UnknownError', { defaultValue: 'Unknown error' });
      if (message.includes('500') || message.includes('Internal Server Error')) {
        setErrorMessage(t('StyleConfigPanel:ServerError500'));
      } else if (message.includes('401') || message.includes('403')) {
        setErrorMessage(t('StyleConfigPanel:PermissionDenied'));
      } else {
        setErrorMessage(t('StyleConfigPanel:FailedSave', { 0: message }));
      }
    } finally {
      setIsSaving(false);
    }
  }, [saveThemeToBackend]);

  const sections: Record<SectionKey, { label: string; content: React.ReactNode }> = {
    colors: {
      label: t('StyleConfigPanel:Colors'),
      content: (
        <>
          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: '0' }}>
            {t('StyleConfigPanel:BrandColors')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:Primary')}
            value={theme.colors.brandPrimary}
            defaultValue={baseTheme.colors.brandPrimary}
            description={t('StyleConfigPanel:PrimaryDesc')}
            onChange={(v) => updateColor('brandPrimary', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:Accent')}
            value={theme.colors.brandAccent}
            defaultValue={baseTheme.colors.brandAccent}
            description={t('StyleConfigPanel:AccentDesc')}
            onChange={(v) => updateColor('brandAccent', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:AccentLight')}
            value={theme.colors.brandAccentLight}
            defaultValue={baseTheme.colors.brandAccentLight}
            description={t('StyleConfigPanel:AccentLightDesc')}
            onChange={(v) => updateColor('brandAccentLight', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:AccentMid')}
            value={theme.colors.brandAccentMid}
            defaultValue={baseTheme.colors.brandAccentMid}
            description={t('StyleConfigPanel:AccentMidDesc')}
            onChange={(v) => updateColor('brandAccentMid', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:DialogColors', { defaultValue: 'Dialog Colors' })}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:DialogBackground', { defaultValue: 'Dialog Background' })}
            value={theme.colors.dialogBackground}
            defaultValue={baseTheme.colors.dialogBackground}
            description={t('StyleConfigPanel:DialogBackgroundDesc', { defaultValue: 'Background color for dialog headers' })}
            onChange={(v) => updateColor('dialogBackground', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:DialogFontColor', { defaultValue: 'Dialog Font Color' })}
            value={theme.colors.dialogFontColor}
            defaultValue={baseTheme.colors.dialogFontColor}
            description={t('StyleConfigPanel:DialogFontColorDesc', { defaultValue: 'Text color for dialog headers, badges, and titles' })}
            onChange={(v) => updateColor('dialogFontColor', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:SemanticColors')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:Success')}
            value={theme.colors.success}
            defaultValue={baseTheme.colors.success}
            description={t('StyleConfigPanel:SuccessDesc')}
            onChange={(v) => updateColor('success', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SuccessLight')}
            value={theme.colors.successLight}
            defaultValue={baseTheme.colors.successLight}
            description={t('StyleConfigPanel:SuccessLightDesc')}
            onChange={(v) => updateColor('successLight', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:Warning')}
            value={theme.colors.warning}
            defaultValue={baseTheme.colors.warning}
            description={t('StyleConfigPanel:WarningDesc')}
            onChange={(v) => updateColor('warning', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:WarningLight')}
            value={theme.colors.warningLight}
            defaultValue={baseTheme.colors.warningLight}
            description={t('StyleConfigPanel:WarningLightDesc')}
            onChange={(v) => updateColor('warningLight', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:Error')}
            value={theme.colors.error}
            defaultValue={baseTheme.colors.error}
            description={t('StyleConfigPanel:ErrorDesc')}
            onChange={(v) => updateColor('error', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:ErrorLight')}
            value={theme.colors.errorLight}
            defaultValue={baseTheme.colors.errorLight}
            description={t('StyleConfigPanel:ErrorLightDesc')}
            onChange={(v) => updateColor('errorLight', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:Info')}
            value={theme.colors.info}
            defaultValue={baseTheme.colors.info}
            description={t('StyleConfigPanel:InfoDesc')}
            onChange={(v) => updateColor('info', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:InfoLight')}
            value={theme.colors.infoLight}
            defaultValue={baseTheme.colors.infoLight}
            description={t('StyleConfigPanel:InfoLightDesc')}
            onChange={(v) => updateColor('infoLight', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:NeutralColors')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:TextPrimary')}
            value={theme.colors.textPrimary}
            defaultValue={baseTheme.colors.textPrimary}
            description={t('StyleConfigPanel:TextPrimaryDesc')}
            onChange={(v) => updateColor('textPrimary', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:TextSecondary')}
            value={theme.colors.textSecondary}
            defaultValue={baseTheme.colors.textSecondary}
            description={t('StyleConfigPanel:TextSecondaryDesc')}
            onChange={(v) => updateColor('textSecondary', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:TextMuted')}
            value={theme.colors.textMuted}
            defaultValue={baseTheme.colors.textMuted}
            description={t('StyleConfigPanel:TextMutedDesc')}
            onChange={(v) => updateColor('textMuted', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:Border')}
            value={theme.colors.border}
            defaultValue={baseTheme.colors.border}
            description={t('StyleConfigPanel:BorderDesc')}
            onChange={(v) => updateColor('border', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:BorderLight')}
            value={theme.colors.borderLight}
            defaultValue={baseTheme.colors.borderLight}
            description={t('StyleConfigPanel:BorderLightDesc')}
            onChange={(v) => updateColor('borderLight', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:PageBackground')}
            value={theme.colors.backgroundPage}
            defaultValue={baseTheme.colors.backgroundPage}
            description={t('StyleConfigPanel:PageBackgroundDesc')}
            onChange={(v) => updateColor('backgroundPage', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:CardBackground')}
            value={theme.colors.backgroundCard}
            defaultValue={baseTheme.colors.backgroundCard}
            description={t('StyleConfigPanel:CardBackgroundDesc')}
            onChange={(v) => updateColor('backgroundCard', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:TableHeader')}
            value={theme.colors.backgroundTableHeader}
            defaultValue={baseTheme.colors.backgroundTableHeader}
            description={t('StyleConfigPanel:TableHeaderDesc')}
            onChange={(v) => updateColor('backgroundTableHeader', v)}
          />
        </>
      ),
    },
    sidebar: {
      label: t('StyleConfigPanel:Sidebar'),
      content: (
        <>
          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: '0' }}>
            {t('StyleConfigPanel:SidebarColors')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:SidebarBackground')}
            value={theme.sidebar.background}
            defaultValue={baseTheme.sidebar.background}
            description={t('StyleConfigPanel:SidebarBackgroundDesc')}
            onChange={(v) => updateSidebar('background', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarText')}
            value={theme.sidebar.text}
            defaultValue={baseTheme.sidebar.text}
            description={t('StyleConfigPanel:SidebarTextDesc')}
            onChange={(v) => updateSidebar('text', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarTextMuted')}
            value={theme.sidebar.textMuted}
            defaultValue={baseTheme.sidebar.textMuted}
            description={t('StyleConfigPanel:SidebarTextMutedDesc')}
            onChange={(v) => updateSidebar('textMuted', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:SidebarInteractive')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:SidebarActiveBackground')}
            value={theme.sidebar.activeBackground}
            defaultValue={baseTheme.sidebar.activeBackground}
            description={t('StyleConfigPanel:SidebarActiveBackgroundDesc')}
            onChange={(v) => updateSidebar('activeBackground', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarActiveText')}
            value={theme.sidebar.activeText}
            defaultValue={baseTheme.sidebar.activeText}
            description={t('StyleConfigPanel:SidebarActiveTextDesc')}
            onChange={(v) => updateSidebar('activeText', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarHoverBackground')}
            value={theme.sidebar.hoverBackground}
            defaultValue={baseTheme.sidebar.hoverBackground}
            description={t('StyleConfigPanel:SidebarHoverBackgroundDesc')}
            onChange={(v) => updateSidebar('hoverBackground', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarBorder')}
            value={theme.sidebar.border}
            defaultValue={baseTheme.sidebar.border}
            description={t('StyleConfigPanel:SidebarBorderDesc')}
            onChange={(v) => updateSidebar('border', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:SidebarIcons')}
          </h4>
          <ColorInput
            label={t('StyleConfigPanel:SidebarIconColor')}
            value={theme.sidebar.iconColor}
            defaultValue={baseTheme.sidebar.iconColor}
            description={t('StyleConfigPanel:SidebarIconColorDesc')}
            onChange={(v) => updateSidebar('iconColor', v)}
          />
          <ColorInput
            label={t('StyleConfigPanel:SidebarIconActiveColor')}
            value={theme.sidebar.iconActiveColor}
            defaultValue={baseTheme.sidebar.iconActiveColor}
            description={t('StyleConfigPanel:SidebarIconActiveColorDesc')}
            onChange={(v) => updateSidebar('iconActiveColor', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:SidebarFontSizes')}
          </h4>
          <TextInput
            label={t('StyleConfigPanel:SidebarFontSizeParent')}
            value={theme.sidebar.fontSizeParent}
            defaultValue={baseTheme.sidebar.fontSizeParent}
            description={t('StyleConfigPanel:SidebarFontSizeParentDesc')}
            onChange={(v) => updateSidebar('fontSizeParent', v)}
          />
          <TextInput
            label={t('StyleConfigPanel:SidebarFontSizeChild')}
            value={theme.sidebar.fontSizeChild}
            defaultValue={baseTheme.sidebar.fontSizeChild}
            description={t('StyleConfigPanel:SidebarFontSizeChildDesc')}
            onChange={(v) => updateSidebar('fontSizeChild', v)}
          />
          <TextInput
            label={t('StyleConfigPanel:SidebarFontSizeHeader')}
            value={theme.sidebar.fontSizeHeader}
            defaultValue={baseTheme.sidebar.fontSizeHeader}
            description={t('StyleConfigPanel:SidebarFontSizeHeaderDesc')}
            onChange={(v) => updateSidebar('fontSizeHeader', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:SidebarMode')}
          </h4>
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.xs }}>
              <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary }}>
                {t('StyleConfigPanel:SidebarMode')}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span style={{ fontSize: theme.typography.fontSizeXs, color: theme.sidebar.sidebarMode !== baseTheme.sidebar.sidebarMode ? theme.colors.brandAccent : theme.colors.textMuted, fontFamily: 'monospace' }}>
                  {theme.sidebar.sidebarMode}
                </span>
                {theme.sidebar.sidebarMode !== baseTheme.sidebar.sidebarMode && (
                  <button
                    onClick={() => applySidebarPreset(baseTheme.sidebar.sidebarMode)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '2px',
                      cursor: 'pointer',
                      color: theme.colors.textMuted,
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: theme.borderRadius.sm,
                    }}
                    title={t('StyleConfigPanel:ResetToDefault')}
                    onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.brandAccent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = theme.colors.textMuted; }}
                  >
                    <Icons.ResetSmall />
                  </button>
                )}
              </div>
            </div>
            <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
              {t('StyleConfigPanel:SidebarModeDesc')}
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm }}>
              <button
                onClick={() => applySidebarPreset('dark')}
                style={{
                  flex: 1,
                  padding: `${theme.spacing.md} ${theme.spacing.md}`,
                  backgroundColor: theme.sidebar.sidebarMode === 'dark' ? theme.colors.brandPrimary : theme.colors.backgroundMuted,
                  color: theme.sidebar.sidebarMode === 'dark' ? 'white' : theme.colors.textSecondary,
                  border: `1px solid ${theme.sidebar.sidebarMode === 'dark' ? theme.colors.brandPrimary : theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSizeSm,
                  fontWeight: theme.typography.fontWeightMedium,
                  cursor: 'pointer',
                  transition: `all ${theme.transitions.default} ease`,
                }}
              >
                {t('StyleConfigPanel:SidebarModeDark')}
              </button>
              <button
                onClick={() => applySidebarPreset('light')}
                style={{
                  flex: 1,
                  padding: `${theme.spacing.md} ${theme.spacing.md}`,
                  backgroundColor: theme.sidebar.sidebarMode === 'light' ? theme.colors.brandPrimary : theme.colors.backgroundMuted,
                  color: theme.sidebar.sidebarMode === 'light' ? 'white' : theme.colors.textSecondary,
                  border: `1px solid ${theme.sidebar.sidebarMode === 'light' ? theme.colors.brandPrimary : theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSizeSm,
                  fontWeight: theme.typography.fontWeightMedium,
                  cursor: 'pointer',
                  transition: `all ${theme.transitions.default} ease`,
                }}
              >
                {t('StyleConfigPanel:SidebarModeLight')}
              </button>
            </div>
          </div>

          {/* Logo Upload Cards */}
          <div style={{ marginTop: theme.spacing.xxl }}>
            <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md }}>
              {t('StyleConfigPanel:LogoUpload')}
            </h4>

            {/* Light Logo Upload */}
            <div style={{ marginBottom: theme.spacing.lg, padding: theme.spacing.md, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
              <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary, display: 'block', marginBottom: theme.spacing.xs }}>
                {t('StyleConfigPanel:LightLogo')}
              </label>
              <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
                {t('StyleConfigPanel:LightLogoDesc')}
              </div>
              {/* Preview */}
              {(lightLogoPreview || theme.sidebar.logoLightUrl) && (
                <div style={{ marginBottom: theme.spacing.md, padding: theme.spacing.sm, backgroundColor: theme.sidebar.background, borderRadius: theme.borderRadius.sm, display: 'inline-block' }}>
                  <img
                    src={lightLogoPreview || theme.sidebar.logoLightUrl}
                    alt={t('StyleConfigPanel:LightLogoPreview', { defaultValue: 'Light logo preview' })}
                    style={{ height: '40px', maxWidth: '180px', objectFit: 'contain' }}
                  />
                </div>
              )}
              <input ref={lightLogoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: 'none' }} onChange={(e) => handleLogoFileSelect(e.target.files?.[0] || null, 'light')} />
              <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                <button
                  onClick={() => lightLogoInputRef.current?.click()}
                  style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.backgroundMuted, cursor: 'pointer' }}
                >
                  {t('StyleConfigPanel:ChooseFile')}
                </button>
                {lightLogoFile && (
                  <button
                    onClick={() => handleLogoUpload('light')}
                    disabled={isUploadingLight}
                    style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: 'none', borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.brandAccent, color: 'white', cursor: isUploadingLight ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}
                  >
                    {isUploadingLight ? <Icons.Spinner /> : null}
                    {t('StyleConfigPanel:UploadLogo')}
                  </button>
                )}
                {theme.sidebar.logoLightUrl && (
                  <button
                    onClick={() => handleLogoRemove('light')}
                    style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.error}`, borderRadius: theme.borderRadius.md, backgroundColor: 'transparent', color: theme.colors.error, cursor: 'pointer' }}
                  >
                    {t('StyleConfigPanel:RemoveLogo')}
                  </button>
                )}
              </div>
            </div>

            {/* Dark Logo Upload */}
            <div style={{ marginBottom: theme.spacing.lg, padding: theme.spacing.md, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
              <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary, display: 'block', marginBottom: theme.spacing.xs }}>
                {t('StyleConfigPanel:DarkLogo')}
              </label>
              <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
                {t('StyleConfigPanel:DarkLogoDesc')}
              </div>
              {/* Preview */}
              {(darkLogoPreview || theme.sidebar.logoDarkUrl) && (
                <div style={{ marginBottom: theme.spacing.md, padding: theme.spacing.sm, backgroundColor: theme.colors.backgroundMuted, borderRadius: theme.borderRadius.sm, display: 'inline-block' }}>
                  <img
                    src={darkLogoPreview || theme.sidebar.logoDarkUrl}
                    alt={t('StyleConfigPanel:DarkLogoPreview', { defaultValue: 'Dark logo preview' })}
                    style={{ height: '40px', maxWidth: '180px', objectFit: 'contain' }}
                  />
                </div>
              )}
              <input ref={darkLogoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: 'none' }} onChange={(e) => handleLogoFileSelect(e.target.files?.[0] || null, 'dark')} />
              <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                <button
                  onClick={() => darkLogoInputRef.current?.click()}
                  style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.backgroundMuted, cursor: 'pointer' }}
                >
                  {t('StyleConfigPanel:ChooseFile')}
                </button>
                {darkLogoFile && (
                  <button
                    onClick={() => handleLogoUpload('dark')}
                    disabled={isUploadingDark}
                    style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: 'none', borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.brandAccent, color: 'white', cursor: isUploadingDark ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}
                  >
                    {isUploadingDark ? <Icons.Spinner /> : null}
                    {t('StyleConfigPanel:UploadLogo')}
                  </button>
                )}
                {theme.sidebar.logoDarkUrl && (
                  <button
                    onClick={() => handleLogoRemove('dark')}
                    style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.error}`, borderRadius: theme.borderRadius.md, backgroundColor: 'transparent', color: theme.colors.error, cursor: 'pointer' }}
                  >
                    {t('StyleConfigPanel:RemoveLogo')}
                  </button>
                )}
              </div>
            </div>

            {/* Login Background Upload */}
            <div style={{ marginBottom: theme.spacing.lg, padding: theme.spacing.md, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
              <label style={{ fontSize: theme.typography.fontSizeSm, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.textPrimary, display: 'block', marginBottom: theme.spacing.xs }}>
                {t('StyleConfigPanel:LoginBackground', { defaultValue: 'Login Background' })}
              </label>
              <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textMuted, marginBottom: theme.spacing.md }}>
                {t('StyleConfigPanel:LoginBackgroundDesc', { defaultValue: 'Displayed behind the sign-in form. Use a landscape PNG, JPEG, or WEBP image.' })}
              </div>
              {(backgroundPreview || backgroundUrl) && (
                <div style={{ marginBottom: theme.spacing.md, width: '100%', aspectRatio: '16 / 7', overflow: 'hidden', borderRadius: theme.borderRadius.sm, border: theme.borders.default }}>
                  <img src={backgroundPreview || backgroundUrl || ''} alt={t('StyleConfigPanel:LoginBackgroundPreview', { defaultValue: 'Login background preview' })} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <input ref={backgroundInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => handleBackgroundFileSelect(e.target.files?.[0] || null)} />
              <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                <button onClick={() => backgroundInputRef.current?.click()} style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.backgroundMuted, cursor: 'pointer' }}>
                  {t('StyleConfigPanel:ChooseFile')}
                </button>
                {backgroundFile && (
                  <button onClick={handleBackgroundUpload} disabled={isUploadingBackground} style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: 'none', borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.brandAccent, color: 'white', cursor: isUploadingBackground ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                    {isUploadingBackground ? <Icons.Spinner /> : null}
                    {t('StyleConfigPanel:UploadBackground', { defaultValue: 'Upload Background' })}
                  </button>
                )}
                {backgroundUrl && (
                  <button onClick={handleBackgroundRemove} style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.typography.fontSizeSm, border: `1px solid ${theme.colors.error}`, borderRadius: theme.borderRadius.md, backgroundColor: 'transparent', color: theme.colors.error, cursor: 'pointer' }}>
                    {t('StyleConfigPanel:RemoveBackground', { defaultValue: 'Remove Background' })}
                  </button>
                )}
              </div>
            </div>

            {/* Info note when both logos are set */}
            {theme.sidebar.logoLightUrl && theme.sidebar.logoDarkUrl && (
              <div style={{ padding: theme.spacing.md, backgroundColor: theme.colors.infoLight, border: `1px solid ${theme.colors.info}`, borderRadius: theme.borderRadius.md, fontSize: theme.typography.fontSizeXs, color: theme.colors.info }}>
                {t('StyleConfigPanel:LogoModeBypassNote')}
              </div>
            )}
          </div>

          {/* Sidebar Preview */}
          <div style={{ marginTop: theme.spacing.xxl }}>
            <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md }}>
              {t('StyleConfigPanel:SidebarPreview')}
            </h4>
            <div
              style={{
                backgroundColor: theme.sidebar.background,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing.md,
                minHeight: '200px',
              }}
            >
              {/* Logo area */}
              <div
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.sm}`,
                  borderBottom: `1px solid ${theme.sidebar.border}`,
                  marginBottom: theme.spacing.md,
                }}
              >
                {(theme.sidebar.logoLightUrl || theme.sidebar.logoDarkUrl) ? (
                  <img
                    src={theme.sidebar.sidebarMode === 'dark'
                      ? (theme.sidebar.logoLightUrl || theme.sidebar.logoDarkUrl)
                      : (theme.sidebar.logoDarkUrl || theme.sidebar.logoLightUrl)}
                    alt={t('StyleConfigPanel:LogoPreview', { defaultValue: 'Logo preview' })}
                    style={{ height: '28px', maxWidth: '160px', objectFit: 'contain' }}
                  />
                ) : (
                  <span style={{ color: theme.sidebar.text, fontWeight: theme.typography.fontWeightBold, fontSize: theme.sidebar.fontSizeHeader }}>
                    mot-r
                  </span>
                )}
              </div>

              {/* Sample menu items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {/* Active item */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                    padding: `${theme.spacing.md} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.md,
                    backgroundColor: theme.sidebar.activeBackground,
                    color: theme.sidebar.activeText,
                    fontSize: theme.sidebar.fontSizeParent,
                    fontWeight: theme.typography.fontWeightSemibold,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.sidebar.iconActiveColor} strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                  {t('Menu:Home')}
                </div>

                {/* Normal item */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                    padding: `${theme.spacing.md} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.md,
                    color: theme.sidebar.textMuted,
                    fontSize: theme.sidebar.fontSizeParent,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.sidebar.iconColor} strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t('Menu:Add')}
                </div>

                {/* Another normal item */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                    padding: `${theme.spacing.md} ${theme.spacing.md}`,
                    borderRadius: theme.borderRadius.md,
                    color: theme.sidebar.textMuted,
                    fontSize: theme.sidebar.fontSizeParent,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.sidebar.iconColor} strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                  {t('Menu:People&Workgroups')}
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
    typography: {
      label: t('StyleConfigPanel:Typography'),
      content: (
        <>
          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: '0' }}>
            {t('StyleConfigPanel:FontFamily')}
          </h4>
          <TextInput
            label={t('StyleConfigPanel:FontFamily')}
            value={theme.typography.fontFamily}
            defaultValue={baseTheme.typography.fontFamily}
            description={t('StyleConfigPanel:PrimaryFontStackDesc')}
            onChange={(v) => updateTypography('fontFamily', v)}
          />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:FontSizes')}
          </h4>
          <TextInput label={t('StyleConfigPanel:XSBadges')} value={theme.typography.fontSizeXs} defaultValue={baseTheme.typography.fontSizeXs} onChange={(v) => updateTypography('fontSizeXs', v)} />
          <TextInput label={t('StyleConfigPanel:SMLabels')} value={theme.typography.fontSizeSm} defaultValue={baseTheme.typography.fontSizeSm} onChange={(v) => updateTypography('fontSizeSm', v)} />
          <TextInput label={t('StyleConfigPanel:BaseCaptions')} value={theme.typography.fontSizeBase} defaultValue={baseTheme.typography.fontSizeBase} onChange={(v) => updateTypography('fontSizeBase', v)} />
          <TextInput label={t('StyleConfigPanel:MDBody')} value={theme.typography.fontSizeMd} defaultValue={baseTheme.typography.fontSizeMd} onChange={(v) => updateTypography('fontSizeMd', v)} />
          <TextInput label={t('StyleConfigPanel:LGTableCells')} value={theme.typography.fontSizeLg} defaultValue={baseTheme.typography.fontSizeLg} onChange={(v) => updateTypography('fontSizeLg', v)} />
          <TextInput label={t('StyleConfigPanel:XLNav')} value={theme.typography.fontSizeXl} defaultValue={baseTheme.typography.fontSizeXl} onChange={(v) => updateTypography('fontSizeXl', v)} />
          <TextInput label={t('StyleConfigPanel:TwoXLHeadings')} value={theme.typography.fontSize2xl} defaultValue={baseTheme.typography.fontSize2xl} onChange={(v) => updateTypography('fontSize2xl', v)} />
          <TextInput label={t('StyleConfigPanel:KPINumbers')} value={theme.typography.fontSizeKpi} defaultValue={baseTheme.typography.fontSizeKpi} onChange={(v) => updateTypography('fontSizeKpi', v)} />

          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: theme.spacing.xxl }}>
            {t('StyleConfigPanel:FontWeights')}
          </h4>
          <TextInput label={t('StyleConfigPanel:Normal')} value={theme.typography.fontWeightNormal} defaultValue={baseTheme.typography.fontWeightNormal} onChange={(v) => updateTypography('fontWeightNormal', parseInt(v) || 400)} />
          <TextInput label={t('StyleConfigPanel:Medium')} value={theme.typography.fontWeightMedium} defaultValue={baseTheme.typography.fontWeightMedium} onChange={(v) => updateTypography('fontWeightMedium', parseInt(v) || 500)} />
          <TextInput label={t('StyleConfigPanel:Semibold')} value={theme.typography.fontWeightSemibold} defaultValue={baseTheme.typography.fontWeightSemibold} onChange={(v) => updateTypography('fontWeightSemibold', parseInt(v) || 600)} />
          <TextInput label={t('StyleConfigPanel:Bold')} value={theme.typography.fontWeightBold} defaultValue={baseTheme.typography.fontWeightBold} onChange={(v) => updateTypography('fontWeightBold', parseInt(v) || 700)} />
        </>
      ),
    },
    spacing: {
      label: t('StyleConfigPanel:Spacing'),
      content: (
        <>
          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: '0' }}>
            {t('StyleConfigPanel:SpacingScale')}
          </h4>
          <TextInput label={t('StyleConfigPanel:SpacingXs', { defaultValue: 'XS (4px)' })} value={theme.spacing.xs} defaultValue={baseTheme.spacing.xs} onChange={(v) => updateSpacing('xs', v)} />
          <TextInput label={t('StyleConfigPanel:SpacingSm', { defaultValue: 'SM (8px)' })} value={theme.spacing.sm} defaultValue={baseTheme.spacing.sm} onChange={(v) => updateSpacing('sm', v)} />
          <TextInput label={t('StyleConfigPanel:SpacingMd', { defaultValue: 'MD (12px)' })} value={theme.spacing.md} defaultValue={baseTheme.spacing.md} onChange={(v) => updateSpacing('md', v)} />
          <TextInput label={t('StyleConfigPanel:SpacingLg', { defaultValue: 'LG (16px)' })} value={theme.spacing.lg} defaultValue={baseTheme.spacing.lg} onChange={(v) => updateSpacing('lg', v)} />
          <TextInput label={t('StyleConfigPanel:SpacingXl', { defaultValue: 'XL (20px)' })} value={theme.spacing.xl} defaultValue={baseTheme.spacing.xl} onChange={(v) => updateSpacing('xl', v)} />
          <TextInput label={t('StyleConfigPanel:SpacingXxl', { defaultValue: 'XXL (24px)' })} value={theme.spacing.xxl} defaultValue={baseTheme.spacing.xxl} onChange={(v) => updateSpacing('xxl', v)} />
        </>
      ),
    },
    borders: {
      label: t('StyleConfigPanel:Borders'),
      content: (
        <>
          <h4 style={{ fontSize: theme.typography.fontSizeBase, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textPrimary, marginBottom: theme.spacing.md, marginTop: '0' }}>
            {t('StyleConfigPanel:BorderRadius')}
          </h4>
          <TextInput label={t('StyleConfigPanel:SMBadges')} value={theme.borderRadius.sm} defaultValue={baseTheme.borderRadius.sm} onChange={(v) => updateBorderRadius('sm', v)} />
          <TextInput label={t('StyleConfigPanel:MDButtons')} value={theme.borderRadius.md} defaultValue={baseTheme.borderRadius.md} onChange={(v) => updateBorderRadius('md', v)} />
          <TextInput label={t('StyleConfigPanel:LGCards')} value={theme.borderRadius.lg} defaultValue={baseTheme.borderRadius.lg} onChange={(v) => updateBorderRadius('lg', v)} />
          <TextInput label={t('StyleConfigPanel:XLPanels')} value={theme.borderRadius.xl} defaultValue={baseTheme.borderRadius.xl} onChange={(v) => updateBorderRadius('xl', v)} />
          <TextInput label={t('StyleConfigPanel:XXLModals')} value={theme.borderRadius.xxl} defaultValue={baseTheme.borderRadius.xxl} onChange={(v) => updateBorderRadius('xxl', v)} />
        </>
      ),
    },
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: theme.colors.overlayBackdropLight,
          zIndex: 299,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '400px',
          maxWidth: '100vw',
          backgroundColor: theme.colors.backgroundCard,
          boxShadow: theme.shadows.modal,
          zIndex: 300,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: theme.typography.fontFamily,
        }}
      >
        {/* Panel Header */}
        <div
          style={{
            padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <h2 style={{ fontSize: theme.typography.fontSizeLg, fontWeight: theme.typography.fontWeightSemibold, margin: 0, color: theme.colors.textPrimary }}>
            {t('StyleConfigPanel:ThemeConfiguration')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              cursor: 'pointer',
              padding: theme.spacing.xs,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={t('StyleConfigPanel:Close')}
          >
            <Icons.Close />
          </button>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            padding: `${theme.spacing.md} ${theme.spacing.xl}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.sm,
            flexShrink: 0,
          }}
        >
          {/* Primary action: Save to Server */}
          <button
            onClick={handleSaveToBackend}
            disabled={!hasOverride || isSaving}
            style={{
              width: '100%',
              padding: `${theme.spacing.md} ${theme.spacing.md}`,
              backgroundColor: saveStatus === 'success' ? theme.colors.success : theme.colors.brandPrimary,
              border: 'none',
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSizeBase,
              color: 'white',
              cursor: hasOverride && !isSaving ? 'pointer' : 'not-allowed',
              fontWeight: theme.typography.fontWeightSemibold,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              opacity: hasOverride && !isSaving ? 1 : 0.6,
              transition: `all ${theme.transitions.default} ease`,
            }}
            title={t('StyleConfigPanel:SaveTooltip')}
          >
            {isSaving ? (
              <>
                <Icons.Spinner />
                {t('StyleConfigPanel:Saving')}
              </>
            ) : saveStatus === 'success' ? (
              <>
                <Icons.Check />
                {t('StyleConfigPanel:Saved')}
              </>
            ) : (
              <>
                <Icons.Save />
                {t('StyleConfigPanel:SaveToServer')}
              </>
            )}
          </button>

          {/* Error message */}
          {saveStatus === 'error' && errorMessage && (
            <div
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: `${theme.colors.error}15`,
                border: `1px solid ${theme.colors.error}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSizeSm,
                color: theme.colors.error,
                lineHeight: 1.4,
              }}
            >
              {errorMessage}
            </div>
          )}

          {/* Secondary actions */}
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <button
              onClick={handleReset}
              disabled={!hasOverride}
              style={{
                flex: 1,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.backgroundTableHeader,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                color: hasOverride ? theme.colors.textSecondary : theme.colors.textMuted,
                cursor: hasOverride ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.md,
              }}
              title={t('StyleConfigPanel:ResetTooltip')}
            >
              <Icons.Reset />
              {t('StyleConfigPanel:Reset')}
            </button>
            <button
              onClick={handleImport}
              style={{
                flex: 1,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.backgroundTableHeader,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.md,
              }}
              title={t('StyleConfigPanel:ImportTooltip')}
            >
              <Icons.Upload />
              {t('StyleConfigPanel:Import')}
            </button>
            <button
              onClick={handleExport}
              style={{
                flex: 1,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.backgroundTableHeader,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.md,
              }}
              title={t('StyleConfigPanel:ExportTooltip')}
            >
              <Icons.Download />
              {t('StyleConfigPanel:Export')}
            </button>
          </div>

          {/* Status message */}
          {saveStatus === 'error' && (
            <div
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.errorLight,
                color: theme.colors.error,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                textAlign: 'center',
              }}
            >
              {t('StyleConfigPanel:FailedSaveRetry')}
            </div>
          )}
        </div>

        {/* Section Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.backgroundTableHeader,
            flexShrink: 0,
          }}
        >
          {(Object.keys(sections) as SectionKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              style={{
                flex: 1,
                padding: `${theme.spacing.md} ${theme.spacing.sm}`,
                border: 'none',
                borderBottom: activeSection === key ? `2px solid ${theme.colors.brandPrimary}` : '2px solid transparent',
                backgroundColor: 'transparent',
                color: activeSection === key ? theme.colors.brandPrimary : theme.colors.textSecondary,
                fontSize: theme.typography.fontSizeXs,
                fontWeight: activeSection === key ? theme.typography.fontWeightSemibold : theme.typography.fontWeightNormal,
                cursor: 'pointer',
              }}
            >
              {sections[key].label}
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: theme.spacing.xl }}>{sections[activeSection].content}</div>

        {/* Live Preview */}
        <div
          style={{
            padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
            borderTop: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.backgroundMuted,
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: theme.typography.fontSizeXs, fontWeight: theme.typography.fontWeightSemibold, color: theme.colors.textSecondary, marginBottom: theme.spacing.md }}>
            {t('StyleConfigPanel:LivePreview')}
          </div>

          {/* Sample KPI Cards */}
          <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.md }}>
            {[
              { label: t('StyleConfigPanel:InProgress'), value: 62, color: theme.colors.info },
              { label: t('StyleConfigPanel:Completed'), value: 47, color: theme.colors.success },
              { label: t('StyleConfigPanel:Overdue'), value: 5, color: theme.colors.error },
            ].map((kpi, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: `${theme.spacing.md} ${theme.spacing.md}`,
                  backgroundColor: theme.colors.backgroundCard,
                  borderRadius: theme.borderRadius.lg,
                  border: `1px solid ${theme.colors.border}`,
                  boxShadow: theme.shadows.card,
                }}
              >
                <div style={{ fontSize: theme.typography.fontSizeXs, color: theme.colors.textSecondary, marginBottom: theme.spacing.xs }}>
                  {kpi.label}
                </div>
                <div style={{ fontSize: theme.typography.fontSizeKpi, fontWeight: theme.typography.fontWeightBold, color: kpi.color }}>
                  {kpi.value}
                </div>
              </div>
            ))}
          </div>

          {/* Button Variants */}
          <div style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap' }}>
            <button
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.md}`,
                backgroundColor: theme.colors.brandPrimary,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                fontWeight: theme.typography.fontWeightMedium,
                cursor: 'pointer',
              }}
            >
              {t('StyleConfigPanel:PreviewPrimary')}
            </button>
            <button
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.md}`,
                backgroundColor: theme.colors.brandAccent,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                fontWeight: theme.typography.fontWeightMedium,
                cursor: 'pointer',
              }}
            >
              {t('StyleConfigPanel:PreviewAccent')}
            </button>
            <button
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.md}`,
                backgroundColor: theme.colors.success,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                fontWeight: theme.typography.fontWeightMedium,
                cursor: 'pointer',
              }}
            >
              {t('StyleConfigPanel:PreviewSuccess')}
            </button>
            <button
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.md}`,
                backgroundColor: theme.colors.error,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeSm,
                fontWeight: theme.typography.fontWeightMedium,
                cursor: 'pointer',
              }}
            >
              {t('StyleConfigPanel:PreviewDanger')}
            </button>
          </div>

          {/* Sample Status Badges */}
          <div style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap', marginTop: theme.spacing.md }}>
            {[
              { label: t('StyleConfigPanel:BadgeSuccess'), bg: theme.colors.successLight, color: theme.colors.success },
              { label: t('StyleConfigPanel:BadgeWarning'), bg: theme.colors.warningLight, color: theme.colors.warning },
              { label: t('StyleConfigPanel:BadgeError'), bg: theme.colors.errorLight, color: theme.colors.error },
              { label: t('StyleConfigPanel:BadgeInfo'), bg: theme.colors.infoLight, color: theme.colors.info },
            ].map((badge, i) => (
              <span
                key={i}
                style={{
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  borderRadius: theme.borderRadius.sm,
                  backgroundColor: badge.bg,
                  color: badge.color,
                  fontSize: theme.typography.fontSizeBase,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                {badge.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default StyleConfigPanel;
